import type { Control, FieldValues, Path } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from "@shared-kernel/primary/shared/shadcn/ui/form";
import { Input } from "@shared-kernel/primary/shared/shadcn/ui/input";
import { LongTextField } from "@shared-kernel/primary/shared/long-text-field/long-text-field";
import { Checkbox } from "@shared-kernel/primary/shared/shadcn/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shared-kernel/primary/shared/shadcn/ui/select";
import { Combobox } from "@shared-kernel/primary/shared/combobox/combobox";
import { PopoverCalendar } from "@shared-kernel/primary/shared/calendar/popover-calendar";
import { Label } from "@shared-kernel/primary/shared/shadcn/ui/label";
import type { FieldConfig } from "./types";

interface FormFieldRendererProps<T extends FieldValues> {
  field: FieldConfig<T>;
  control: Control<T>;
  now?: Date;
}

export function FormFieldRenderer<T extends FieldValues>({ field, control, now = new Date() }: FormFieldRendererProps<T>) {
  const name = field.name as Path<T>;

  switch (field.type) {
    case "text":
    case "email":
    case "password":
      return (
        <FormField
          control={control}
          name={name}
          render={({ field: formField }) => (
            <FormItem className={field.className}>
              <FormLabel>{field.label}</FormLabel>
              <FormControl>
                <Input
                  {...formField}
                  type={field.type}
                  placeholder={field.placeholder}
                  disabled={field.disabled}
                  maxLength={field.maxLength}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      );

    case "number":
      return (
        <FormField
          control={control}
          name={name}
          render={({ field: formField }) => (
            <FormItem className={field.className}>
              <FormLabel>{field.label}</FormLabel>
              {field.description && <FormDescription>{field.description}</FormDescription>}
              <FormControl>
                <Input
                  {...formField}
                  type="number"
                  placeholder={field.placeholder}
                  disabled={field.disabled}
                  min={field.min}
                  max={field.max}
                  step={field.step}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      );

    case "textarea":
      return (
        <LongTextField
          control={control}
          name={name}
          label={field.label}
          placeholder={field.placeholder}
          disabled={field.disabled}
          rows={field.rows}
          itemClassName={field.className}
        />
      );

    case "checkbox":
      return (
        <FormField
          control={control}
          name={name}
          render={({ field: formField }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md">
              <FormControl>
                <Checkbox checked={formField.value} onCheckedChange={formField.onChange} disabled={field.disabled} />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>{field.label}</FormLabel>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      );

    case "select":
      return (
        <FormField
          control={control}
          name={name}
          render={({ field: formField }) => (
            <FormItem className={field.className}>
              <FormLabel>{field.label}</FormLabel>
              <Select onValueChange={formField.onChange} value={formField.value ?? ""} disabled={field.disabled}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={field.placeholder} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {field.options.map((option: { value: string; label: string }) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      );

    case "combobox":
      return (
        <FormField
          control={control}
          name={name}
          render={({ field: formField }) => (
            <FormItem className={field.className}>
              <FormLabel>{field.label}</FormLabel>
              <FormControl>
                <Combobox
                  options={field.options}
                  value={formField.value}
                  onChange={formField.onChange}
                  placeholder={field.placeholder ?? ""}
                  search={field.search}
                  disabled={field.disabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      );

    case "date":
      return (
        <FormField
          control={control}
          name={name}
          render={({ field: formField }) => (
            <FormItem className="flex flex-col">
              <FormLabel>{field.label}</FormLabel>
              <PopoverCalendar
                value={formField.value}
                onChange={formField.onChange}
                now={now}
                minDate={field.minDate}
                maxDate={field.maxDate}
                disabled={field.disabled}
              />
              <FormMessage />
            </FormItem>
          )}
        />
      );

    case "display":
      return (
        <div className="space-y-2">
          <Label>{field.label}</Label>
          <Input value="" disabled />
        </div>
      );

    case "custom":
      return field.render({ control, name });

    default:
      return null;
  }
}
